<template>
  <!--
    Drawer + scrim must NOT live inside .navigation: backdrop-filter on the bar
    creates a containing block, so position:fixed children were clipped to ~64px tall.
  -->
  <div class="site-nav">
    <div class="site-nav-bar">
      <nav class="navigation" aria-label="Site">
        <div class="nav-container">
          <router-link to="/" class="logo" @click="onLogoNav">
            <span class="logo-mark" aria-hidden="true" />
            <span class="logo-wordmark">
              <span class="logo-text">The Circle</span>
              <span class="logo-sub">Private Estate</span>
            </span>
          </router-link>

          <button
            type="button"
            class="nav-toggle"
            :aria-expanded="navDrawerOpen ? 'true' : 'false'"
            aria-controls="primary-navigation"
            :aria-label="navDrawerOpen ? 'Close menu' : 'Open menu'"
            @click="toggleDrawer"
          >
            <span class="nav-toggle-bars" :class="{ open: navDrawerOpen }">
              <span />
              <span />
              <span />
            </span>
          </button>
        </div>
      </nav>

      <nav
        id="primary-navigation"
        class="nav-links"
        aria-label="Main"
        :class="{ 'is-open': navDrawerOpen }"
      >
        <router-link to="/" class="nav-link" @click="closeDrawer">Home</router-link>
        <router-link to="/activities" class="nav-link" @click="closeDrawer">Activities</router-link>
        <router-link to="/moon" class="nav-link" @click="closeDrawer">Moon</router-link>
        <router-link to="/map" class="nav-link" @click="closeDrawer">Map</router-link>

        <div class="dropdown" :class="{ 'is-open': isDropdownOpen }">
          <button
            type="button"
            class="nav-link dropdown-toggle"
            :aria-expanded="isDropdownOpen ? 'true' : 'false'"
            aria-haspopup="true"
            @click="toggleDropdown"
          >
            <span>All activities</span>
            <svg class="chevron" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path
                d="M3 4.5L6 7.5L9 4.5"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <div class="dropdown-menu" role="menu">
            <router-link
              to="/activities/escape-room"
              class="dropdown-item"
              role="menuitem"
              @click="closeDropdownAndDrawer"
            >
              Escape Room
            </router-link>
            <router-link
              to="/activities/open-cinema"
              class="dropdown-item"
              role="menuitem"
              @click="closeDropdownAndDrawer"
            >
              Open Cinema
            </router-link>
            <router-link
              to="/activities/bvumbwe-hike"
              class="dropdown-item"
              role="menuitem"
              @click="closeDropdownAndDrawer"
            >
              Bvumbwe Hike &amp; Trail Runs
            </router-link>
            <router-link
              to="/activities/culinary-experience"
              class="dropdown-item"
              role="menuitem"
              @click="closeDropdownAndDrawer"
            >
              Village Culinary Experience
            </router-link>
            <router-link
              to="/activities/farmers-market"
              class="dropdown-item"
              role="menuitem"
              @click="closeDropdownAndDrawer"
            >
              Farmers Market
            </router-link>
            <router-link
              to="/moon"
              class="dropdown-item"
              role="menuitem"
              @click="closeDropdownAndDrawer"
            >
              Moon — Luxury Getaway
            </router-link>
          </div>
        </div>
      </nav>
    </div>

    <div
      v-show="navDrawerOpen"
      class="nav-scrim"
      aria-hidden="true"
      @click="closeDrawer"
    />
  </div>
</template>

<script>
const NAV_DRAWER_MQ = '(max-width: 900px)'

export default {
  name: 'NavigationBar',
  data() {
    return {
      isDropdownOpen: false,
      navDrawerOpen: false,
      narrowNav: false
    }
  },
  mounted() {
    this._mq = window.matchMedia(NAV_DRAWER_MQ)
    this.narrowNav = this._mq.matches
    this._onMq = (e) => {
      this.narrowNav = e.matches
      if (!e.matches) {
        this.navDrawerOpen = false
        document.body.classList.remove('nav-drawer-locked')
      }
    }
    this._mq.addEventListener('change', this._onMq)
    this._onKeydown = (e) => {
      if (e.key === 'Escape') {
        this.closeDropdown()
        this.closeDrawer()
      }
    }
    document.addEventListener('keydown', this._onKeydown)
  },
  beforeUnmount() {
    if (this._mq) this._mq.removeEventListener('change', this._onMq)
    document.removeEventListener('keydown', this._onKeydown)
    document.body.classList.remove('nav-drawer-locked')
  },
  watch: {
    $route() {
      this.closeDropdown()
      this.closeDrawer()
    },
    navDrawerOpen(open) {
      if (typeof document === 'undefined') return
      document.body.classList.toggle('nav-drawer-locked', open && this.narrowNav)
    }
  },
  methods: {
    toggleDrawer() {
      this.navDrawerOpen = !this.navDrawerOpen
      if (!this.navDrawerOpen) this.isDropdownOpen = false
    },
    closeDrawer() {
      this.navDrawerOpen = false
      this.isDropdownOpen = false
    },
    onLogoNav() {
      this.closeDrawer()
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen
    },
    closeDropdown() {
      this.isDropdownOpen = false
    },
    closeDropdownAndDrawer() {
      this.closeDropdown()
      this.closeDrawer()
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

.site-nav {
  position: sticky;
  top: 0;
  z-index: 1000;
  font-family: 'Inter', system-ui, sans-serif;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.site-nav-bar {
  box-sizing: border-box;
}

.navigation {
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.6) inset;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 64px;
  height: auto;
  padding-top: 12px;
  padding-bottom: 12px;
  box-sizing: border-box;
}

@media (min-width: 901px) {
  .site-nav {
    background: rgba(255, 255, 255, 0.78);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 1px 0 rgba(255, 255, 255, 0.6) inset;
  }

  .site-nav-bar {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px;
    min-height: 64px;
    width: 100%;
  }

  .navigation {
    flex: 0 0 auto;
    background: transparent;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    border: none;
    box-shadow: none;
  }

  .nav-container {
    padding: 12px 0;
    margin: 0;
    max-width: none;
    min-height: 0;
    width: auto;
  }
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: #0a0a0a;
  transition: opacity 0.2s ease;
  z-index: 1002;
}

.logo:hover {
  opacity: 0.82;
}

.logo-mark {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1a1a1a 0%, #4a4a4a 100%);
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
}

.logo-wordmark {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  line-height: 1.15;
}

.logo-text {
  font-size: 1.125rem;
  font-weight: 700;
  letter-spacing: -0.03em;
}

.logo-sub {
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.42);
}

@media (min-width: 901px) {
  .logo-wordmark {
    flex-direction: row;
    align-items: baseline;
    gap: 10px;
  }

  .logo-sub {
    padding-top: 0.15em;
  }
}

.nav-toggle {
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.05);
  cursor: pointer;
  z-index: 1002;
  transition: background 0.2s ease;
}

.nav-toggle:hover {
  background: rgba(0, 0, 0, 0.08);
}

.nav-toggle-bars {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 20px;
}

.nav-toggle-bars span {
  display: block;
  height: 2px;
  border-radius: 1px;
  background: #111;
  transition:
    transform 0.25s ease,
    opacity 0.2s ease;
  transform-origin: center;
}

.nav-toggle-bars.open span:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.nav-toggle-bars.open span:nth-child(2) {
  opacity: 0;
  transform: scaleX(0);
}

.nav-toggle-bars.open span:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 6px;
  list-style: none;
  margin: 0;
  padding: 0;
}

@media (min-width: 901px) {
  .nav-links {
    flex: 1 1 auto;
    justify-content: flex-end;
    min-width: 0;
  }
}

.nav-link {
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.55);
  text-decoration: none;
  position: relative;
  padding: 10px 14px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: transparent;
  transition:
    color 0.2s ease,
    background 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.nav-link:hover {
  color: #111;
  background: rgba(0, 0, 0, 0.05);
}

.nav-link.router-link-active {
  color: #111;
  background: rgba(0, 0, 0, 0.07);
}

.nav-link.router-link-active::after {
  display: none;
}

.dropdown-toggle .chevron {
  transition: transform 0.25s ease;
  opacity: 0.65;
}

.dropdown.is-open .dropdown-toggle .chevron {
  transform: rotate(180deg);
}

.dropdown {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  min-width: 280px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.06),
    0 20px 40px -12px rgba(0, 0, 0, 0.15);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-8px) scale(0.98);
  transform-origin: top right;
  transition:
    opacity 0.22s ease,
    transform 0.22s ease,
    visibility 0.22s;
  pointer-events: none;
}

.dropdown.is-open .dropdown-menu {
  opacity: 1;
  visibility: visible;
  transform: translateY(0) scale(1);
  pointer-events: auto;
}

.dropdown-item {
  display: block;
  padding: 11px 14px;
  margin: 2px 0;
  color: #333;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: -0.01em;
  text-transform: none;
  border-radius: 10px;
  transition: background 0.15s ease;
  text-align: left;
}

.dropdown-item:hover {
  background: rgba(0, 0, 0, 0.05);
}

.dropdown-item.router-link-active {
  background: rgba(0, 0, 0, 0.07);
  color: #111;
  font-weight: 600;
}

.nav-scrim {
  display: none;
}

@media (max-width: 900px) {
  .site-nav-bar {
    display: contents;
  }

  .nav-toggle {
    display: flex;
  }

  .nav-scrim {
    display: block;
    position: fixed;
    inset: 0;
    top: 0;
    background: rgba(10, 12, 18, 0.45);
    backdrop-filter: blur(3px);
    z-index: 1000;
    animation: navScrimIn 0.25s ease forwards;
  }

  @keyframes navScrimIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .nav-links {
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    width: 100%;
    max-width: 100%;
    min-height: 100vh;
    min-height: 100dvh;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    gap: 2px;
    padding: max(12px, env(safe-area-inset-top, 0px)) 20px max(20px, env(safe-area-inset-bottom, 0px));
    margin: 0;
    box-sizing: border-box;
    background: rgba(252, 252, 252, 0.98);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    box-shadow: none;
    transform: translateX(100%);
    transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
    z-index: 1001;
    overflow-x: hidden;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
  }

  .nav-links.is-open {
    transform: translateX(0);
  }

  .nav-link {
    justify-content: flex-start;
    width: 100%;
    border-radius: 12px;
    font-size: 0.78rem;
    padding: 11px 14px;
    flex-shrink: 0;
  }

  .dropdown {
    width: 100%;
    flex-shrink: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    margin-top: 6px;
    padding-top: 8px;
  }

  .dropdown-toggle {
    width: 100%;
    justify-content: space-between;
  }

  .dropdown-menu {
    position: static;
    opacity: 1;
    visibility: visible;
    transform: none;
    box-shadow: none;
    border: none;
    background: rgba(0, 0, 0, 0.03);
    border-radius: 12px;
    backdrop-filter: none;
    max-height: 0;
    overflow: hidden;
    padding: 0;
    margin-top: 4px;
    pointer-events: none;
    transition: max-height 0.3s ease;
  }

  .dropdown.is-open .dropdown-menu {
    max-height: min(38vh, 280px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    pointer-events: auto;
    padding: 4px 6px;
  }

  .dropdown-item {
    padding: 9px 10px;
    margin: 0;
    border-radius: 8px;
    font-size: 0.86rem;
  }
}

@media (max-width: 900px) and (max-height: 520px) {
  .nav-links {
    justify-content: flex-start;
    padding-top: max(6px, env(safe-area-inset-top, 0px));
    overflow-y: auto;
  }

  .nav-link {
    padding: 7px 12px;
    font-size: 0.72rem;
  }

  .dropdown-item {
    padding: 6px 8px;
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .nav-container {
    padding-left: 16px;
    padding-right: 16px;
  }

  .logo-text {
    font-size: 1rem;
  }

  .logo-sub {
    font-size: 0.65rem;
  }
}
</style>
